import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserData } from '../services/database';
import { auth, db } from '../services/firebase';
import { doc, setDoc } from 'firebase/firestore';
import AppNavBar from './AppNavBar';
import { useModal } from '../contexts/ModalContext';

const ResourceGeneratorForm = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showAlert } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    resourceType: '',
    subject: '',
    yearGroup: '',
    topic: '',
    requirements: '',
    specialRequirements: '',
    customSubject: '',
    customYearGroup: '',
    customResourceType: '',
    // COMMENTED OUT: Image functionality temporarily disabled
    // addImage: false
  });

  const resourceTypes = [
    'Worksheet', 'Activity Sheet', 'Assessment Questions', 'Homework Tasks', 
    'Revision Guide', 'Practice Problems', 'Discussion Questions', 'Lab Instructions',
    'Project Brief', 'Study Guide', 'Quiz', 'Other'
  ];

  const subjects = [
    'Mathematics', 'English', 'Science', 'History', 'Geography', 'Art', 'Music', 
    'PE/Sports', 'Computing/IT', 'Design Technology', 'Modern Foreign Languages',
    'Religious Education', 'PSHE', 'Drama', 'Business Studies', 'Economics', 'Other'
  ];

  const yearGroups = [
    'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 
    'Year 12', 'Year 13', 'Primary KS1', 'Primary KS2', 'College Level 1',
    'College Level 2', 'College Level 3', 'University', 'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // NEW: Update usage tracking function for resource generation
  const updateResourceUsage = async () => {
    if (!currentUser) {
      console.log('❌ No current user for resource usage tracking');
      return;
    }

    try {
      console.log('📊 Starting resource generation usage tracking...');
      console.log('Current user ID:', currentUser.uid);
      
      const currentUserData = await getUserData(currentUser.uid);
      console.log('Current user data:', currentUserData);
      
      if (currentUserData) {
        // Use the same plannerUsage field as lesson planning to share the quota
        const currentPlannerUsage = currentUserData.plannerUsage || 0;
        console.log(`Current planner usage (includes resources): ${currentPlannerUsage}`);
        
        const updatedData = {
          ...currentUserData,
          plannerUsage: currentPlannerUsage + 1,
          lastPlanning: new Date().toISOString() // Same timestamp field
        };
        
        console.log(`Updating planner usage (resource): ${currentPlannerUsage} → ${currentPlannerUsage + 1}`);
        
        await setDoc(doc(db, 'users', currentUser.uid), updatedData);
        
        console.log('✅ Resource generation usage updated successfully!');
        console.log(`New planner usage: ${currentPlannerUsage + 1}`);
      } else {
        console.log('❌ No user data found for usage tracking');
      }
    } catch (usageError) {
      console.error('❌ Failed to update resource usage tracking:', usageError);
      console.error('Usage error details:', {
        message: usageError.message,
        stack: usageError.stack
      });
      // Don't block the user experience for usage tracking errors
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.resourceType || !formData.subject || !formData.yearGroup || !formData.topic || !formData.requirements) {
      await showAlert('Please fill in all required fields', 'Missing Fields');
      return;
    }

    // Validate custom fields if "Other" is selected
    if (formData.subject === 'Other' && !formData.customSubject.trim()) {
      await showAlert('Please specify the subject', 'Missing Fields');
      return;
    }

    if (formData.yearGroup === 'Other' && !formData.customYearGroup.trim()) {
      await showAlert('Please specify the year group', 'Missing Fields');
      return;
    }

    if (formData.resourceType === 'Other' && !formData.customResourceType.trim()) {
      await showAlert('Please specify the resource type', 'Missing Fields');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🤖 Starting resource generation...');
      console.log('Form data:', formData);
      
      // Prepare final form data with custom values
      const finalFormData = {
        ...formData,
        subject: formData.subject === 'Other' ? formData.customSubject : formData.subject,
        yearGroup: formData.yearGroup === 'Other' ? formData.customYearGroup : formData.yearGroup,
        resourceType: formData.resourceType === 'Other' ? formData.customResourceType : formData.resourceType,
      };

      // Generate resource directly without image
      const response = await fetch('/api/generate-resource', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalFormData),
      });

      console.log('📡 Resource API response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to generate resource');
      }

      const result = await response.json();
      console.log('✅ Resource generated successfully!');
      
      // NEW: Update usage tracking AFTER successful resource generation
      console.log('📊 Updating resource generation usage tracking...');
      await updateResourceUsage();
      
      // Navigate to results page with resource data
      navigate('/planner/resources/result', { 
        state: { 
          resourceContent: result.resourceContent, 
          formData: finalFormData,
          selectedImage: null
        } 
      });
      
    } catch (error) {
      console.error('❌ Error generating resource:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      await showAlert('Sorry, there was an error generating your resource. Please try again.', 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/planner');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="planner" />
      
      <div className="flex items-center justify-center py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight mb-4">
              Create Teaching Resources
            </h1>
            <p className="text-lg text-ink-700">Generate worksheets, activities, and materials with AI assistance</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Required Fields Row */}
              <div className="grid md:grid-cols-2 gap-6">
                
                {/* Resource Type */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Resource Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="resourceType"
                    value={formData.resourceType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select resource type</option>
                    {resourceTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  
                  {/* Custom Resource Type Field */}
                  {formData.resourceType === 'Other' && (
                    <input
                      type="text"
                      name="customResourceType"
                      value={formData.customResourceType}
                      onChange={handleInputChange}
                      placeholder="Please specify the resource type"
                      className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent mt-3"
                      required
                    />
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a subject</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                  
                  {/* Custom Subject Field */}
                  {formData.subject === 'Other' && (
                    <input
                      type="text"
                      name="customSubject"
                      value={formData.customSubject}
                      onChange={handleInputChange}
                      placeholder="Please specify the subject"
                      className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent mt-3"
                      required
                    />
                  )}
                </div>
              </div>

              {/* Year Group */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Year Group / Level <span className="text-red-500">*</span>
                </label>
                <select
                  name="yearGroup"
                  value={formData.yearGroup}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  required
                >
                  <option value="">Select year group or level</option>
                  {yearGroups.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                
                {/* Custom Year Group Field */}
                {formData.yearGroup === 'Other' && (
                  <input
                    type="text"
                    name="customYearGroup"
                    value={formData.customYearGroup}
                    onChange={handleInputChange}
                    placeholder="Please specify the year group or level"
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent mt-3"
                    required
                  />
                )}
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Topic / Focus Area <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  placeholder="e.g., Quadratic Equations, Shakespeare's Macbeth, Computer Hardware Components"
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Requirements */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Resource Requirements <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  placeholder="Describe what you want in your resource. E.g., '10 questions about Macbeth themes with space for annotations', '5 algebra problems with step-by-step solutions', 'Hardware identification worksheet with labeling exercises'..."
                  rows="4"
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent resize-none"
                  required
                />
              </div>

              {/* Optional Fields */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-ink-800 mb-4">Optional Details</h3>

                {/* Special Requirements */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Special Requirements
                  </label>
                  <textarea
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleInputChange}
                    placeholder="e.g., Large font for SEN students, simplified language, specific formatting requirements, accessibility considerations..."
                    rows="3"
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-6 border-t">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-colors font-medium"
                >
                  ← Back to Planner
                </button>
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    isLoading 
                      ? 'bg-ink-400 text-white cursor-not-allowed' 
                      : 'bg-coral-300 text-white hover:bg-coral-500 transform hover:-translate-y-0.5'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                      Creating Your Resource...
                    </div>
                  ) : (
                    'Generate Resource →'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Usage Info */}
          <div className="mt-6 text-center">
            <div className="bg-white bg-opacity-20 rounded-lg border-4 border-ink-900 p-4 max-w-xl mx-auto">
              <p className="text-ink-700 text-sm">
                <span className="font-semibold">✨ Each resource includes:</span> Customized content for your level and professional formatting
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceGeneratorForm;